import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';

import Label from '../../../components/Label';
import { fDateTime } from '../../../utils/formatTime';
import MetricCard from './MetricCard';
import { EmptyModuleState, LoadingPanel, SectionError } from './ManageUserDialogStates';
import { getAccountStateColor } from './utils';
import { fetchAdmin, postAdmin, toErrorMessage } from '../../../admin/api';


// //   // control codes should be a dropdown that and one of the below
// Add meaning explanation for each control on the dropdown list

// Use block_outward_transfer when the user must not move value out at all.
// Use block_bank_transfer when only local bank payout/transfer should stop.
// Use block_bills_vtu when bills/topup access should stop without blocking other flows.
// Use block_giftcard_purchase when giftcard buying should stop without freezing all outward actions.
// Use block_crypto_settlement_to_bank when crypto-to-fiat bank settlement should stop, but other actions may continue.
// use block_giftcard_redemption use to block user from redeeming a giftcard


// const CONTROL_CODES = [
//   { value: 'block_outward_transfer', label: 'Block outward transfer' },
//   { value: 'block_bank_transfer', label: 'Block bank transfer' },
//   { value: 'block_bills_vtu', label: 'Block bills / VTU' },
//   { value: 'block_giftcard_purchase', label: 'Block giftcard purchase' },
//   { value: 'block_crypto_settlement_to_bank', label: 'Block crypto settlement to bank' },
//   { value: 'block_giftcard_redemption', label: 'Block giftcard redemption' },
// ];

// function ControlsCreateDialog({ open, onClose, onSubmit, submitting, value, onChange }) {
//   return (
//     <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
//       <DialogTitle sx={{ pr: 6 }}>
//         <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
//           <Box>
//             <Typography variant="h6">Create Control</Typography>
//             <Typography variant="body2" color="text.secondary">
//               Add a new user control and set an expiry if needed.
//             </Typography>
//           </Box>
//           <IconButton onClick={onClose} disabled={submitting} aria-label="close">
//             <CloseIcon />
//           </IconButton>
//         </Stack>
//       </DialogTitle>

//       <Divider />

//       <DialogContent sx={{ pt: 3 }}>
//         <Stack spacing={2.25}>
//           <TextField
//             select
//             fullWidth
//             label="Control code"
//             value={value.control_code}
//             onChange={(event) => onChange((prev) => ({ ...prev, control_code: event.target.value }))}
//           >
//             {CONTROL_CODES.map((option) => (
//               <MenuItem key={option.value} value={option.value}>
//                 {option.label}
//               </MenuItem>
//             ))}
//           </TextField>

//           <TextField
//             fullWidth
//             multiline
//             minRows={3}
//             label="Reason"
//             value={value.reason}
//             onChange={(event) => onChange((prev) => ({ ...prev, reason: event.target.value }))}
//           />

//           <TextField
//             fullWidth
//             type="datetime-local"
//             label="Expires at"
//             value={value.expires_at}
//             onChange={(event) => onChange((prev) => ({ ...prev, expires_at: event.target.value }))}
//             InputLabelProps={{ shrink: true }}
//             helperText="Optional. Leave blank for no expiry."
//           />
//         </Stack>
//       </DialogContent>

//       <DialogActions sx={{ px: 3, pb: 3 }}>
//         <LoadingButton onClick={onClose} variant="outlined" disabled={submitting}>
//           Cancel
//         </LoadingButton>
//         <LoadingButton onClick={onSubmit} variant="contained" loading={submitting}>
//           Add Control
//         </LoadingButton>
//       </DialogActions>
//     </Dialog>
//   );
// }




const CONTROL_CODES = [
  {
    value: 'block_outward_transfer',
    label: 'Block outward transfer',
    description: 'Use when the user must not move value out at all.',
  },
  {
    value: 'block_bank_transfer',
    label: 'Block bank transfer',
    description: 'Use when only local bank payout or transfer should stop.',
  },
  {
    value: 'block_bills_vtu',
    label: 'Block bills / VTU',
    description: 'Use when bills and top-up access should stop without blocking other flows.',
  },
  {
    value: 'block_giftcard_purchase',
    label: 'Block giftcard purchase',
    description: 'Use when giftcard buying should stop without freezing all outward actions.',
  },
  {
    value: 'block_crypto_settlement_to_bank',
    label: 'Block crypto settlement to bank',
    description: 'Use when crypto-to-fiat bank settlement should stop, but other actions may continue.',
  },
  {
    value: 'block_giftcard_redemption',
    label: 'Block giftcard redemption',
    description: 'Use to block a user from redeeming a giftcard.',
  },
];

function ControlOptionPreview({ option, selected }) {
  return (
    <Stack spacing={0.35} sx={{ py: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {option.label}
        </Typography>
        {selected ? <Chip size="small" color="primary" label="Selected" /> : null}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
        {option.description}
      </Typography>
      {/* <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
        {option.value}
      </Typography> */}
    </Stack>
  );
}

ControlOptionPreview.propTypes = {
  option: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  selected: PropTypes.bool,
};

function ControlsCreateDialog({ open, onClose, onSubmit, submitting, value, onChange }) {
  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="h6">Create Control</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Choose the most specific control code to match the action you want to restrict.
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={submitting} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2.5}>
          <FormControl fullWidth>
            <TextField
              select
              fullWidth
              label="Control code"
              value={value.control_code}
              onChange={(event) => onChange((prev) => ({ ...prev, control_code: event.target.value }))}
              helperText="Pick the narrowest control that matches the restriction."
              SelectProps={{
                renderValue: (selected) => {
                  const option = CONTROL_CODES.find((item) => item.value === selected);
                  if (!option) return 'Select a control';
                  return (
                    <Stack spacing={0.2}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Stack>
                  );
                },
              }}
            >
              {CONTROL_CODES.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ py: 1.25, whiteSpace: 'normal' }}>
                  <ControlOptionPreview option={option} selected={option.value === value.control_code} />
                </MenuItem>
              ))}
            </TextField>
          </FormControl>

          {/* {selectedOption ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.neutral',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Control guidance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedOption.description}
              </Typography>
            </Box>
          ) : null} */}

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            value={value.reason}
            onChange={(event) => onChange((prev) => ({ ...prev, reason: event.target.value }))}
            placeholder="Example: Fraud review, chargeback risk, KYC escalation, suspicious activity."
          />

          <TextField
            fullWidth
            type="datetime-local"
            label="Expires at"
            value={value.expires_at}
            onChange={(event) => onChange((prev) => ({ ...prev, expires_at: event.target.value }))}
            InputLabelProps={{ shrink: true }}
            helperText="Optional. Leave blank for no expiry."
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <LoadingButton onClick={onClose} variant="outlined" disabled={submitting}>
          Cancel
        </LoadingButton>
        <LoadingButton onClick={onSubmit} variant="contained" loading={submitting}>
          Add Control
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

ControlsCreateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  value: PropTypes.shape({
    control_code: PropTypes.string,
    reason: PropTypes.string,
    expires_at: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};



export default function ControlsTab({ userId }) {
  const { enqueueSnackbar } = useSnackbar();

  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);


  const [total, setTotal] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [controlForm, setControlForm] = useState({
    control_code: '',
    reason: '',
    expires_at: '',
  });

  const loadControls = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetchAdmin(`/users/${userId}/controls`);

      const nextControls = res?.controls ?? res?.data?.controls ?? res?.data ?? [];
      // const pagination = res?.pagination ?? res?.data?.pagination ?? {};

      setControls(Array.isArray(nextControls) ? nextControls : []);
      setTotal(Number(nextControls.length ?? 0));
      setLoaded(true);
    } catch (err) {
      setError(err?.message || 'Failed to load user controls.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadControls();
  }, [loadControls, refreshKey]);

  const activeCount = useMemo(
    () => controls.filter((item) => item.status === 'active').length,
    [controls]
  );

  const resolvedCount = useMemo(
    () => controls.filter((item) => item.status === 'resolved').length,
    [controls]
  );

  const resetForm = () => {
    setControlForm({ control_code: '', reason: '', expires_at: '' });
  };

  const handleCreate = async () => {
    if (!controlForm.control_code || !controlForm.reason) {
      enqueueSnackbar('Control code and reason are required.', { variant: 'warning' });
      return;
    }

    setCreateSubmitting(true);
    try {
      const payload = {
        control_code: controlForm.control_code,
        reason: controlForm.reason,
        expires_at: controlForm.expires_at ? controlForm.expires_at.replace('T', ' ') + ':00' : null,
      };

      const res = await postAdmin(`/users/${userId}/controls`, payload);

      if (!res?.status) {
        throw new Error('Control creation failed.');
      }

      enqueueSnackbar('Control created', { variant: 'success' });
      setCreateOpen(false);
      resetForm();
      setRefreshKey((v) => v + 1);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setCreateSubmitting(false);
    }
  };

  const resolveControl = async (controlId) => {
    try {
      await postAdmin(`/users/controls/${controlId}/resolve`);
      enqueueSnackbar('Control resolved', { variant: 'success' });
      setRefreshKey((v) => v + 1);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  if (loading && !loaded) {
    return <LoadingPanel />;
  }

  if (error) {
    return <SectionError message={error} onRetry={loadControls} />;
  }

  return (
    <Stack spacing={3}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Total Controls"
            value={total}
            caption="Control records returned for this user."
            icon="solar:shield-user-bold"
            color="primary"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Active Controls"
            value={activeCount}
            caption="Currently enforced user controls."
            icon="solar:shield-cross-bold"
            color="error"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Resolved Controls"
            value={resolvedCount}
            caption="Control rows that have already been resolved."
            icon="solar:check-circle-bold"
            color="success"
          />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
          <Box>
            <Typography variant="h6">User Controls</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage account restrictions and review active enforcement states.
            </Typography>
          </Box>
          <LoadingButton variant="contained" onClick={() => setCreateOpen(true)}>
            Add Control
          </LoadingButton>
        </Stack>

        <Divider />

        {controls.length ? (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Control</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Resolved</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {controls.map((row) => {
                    const isActive = row.status === 'active';

                    return (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2">{row.control_code}</Typography>
                        </TableCell>
                        <TableCell>
                          <Label color={getAccountStateColor(row.status)}>{row.status || 'unknown'}</Label>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {row.reason || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.source || 'N/A'}</TableCell>
                        <TableCell>{row.expires_at ? fDateTime(row.expires_at) : 'No expiry'}</TableCell>
                        <TableCell>{row.created_at ? fDateTime(row.created_at) : 'N/A'}</TableCell>
                        <TableCell>{row.resolved_at ? fDateTime(row.resolved_at) : 'Not resolved'}</TableCell>
                        <TableCell align="right">
                          {isActive ? (
                            <LoadingButton variant="outlined" size="small" onClick={() => resolveControl(row.id)}>
                              Resolve
                            </LoadingButton>
                          ) : (
                            <Chip size="small" label="Completed" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />

          </>
        ) : (
          <Box sx={{ p: 3 }}>
            <EmptyModuleState
              title="No controls"
              description="No documented control rows were returned for this user."
              icon="solar:shield-user-bold"
            />
          </Box>
        )}
      </Card>

      <ControlsCreateDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          resetForm();
        }}
        onSubmit={handleCreate}
        submitting={createSubmitting}
        value={controlForm}
        onChange={setControlForm}
      />
    </Stack>
  );
}

ControlsTab.propTypes = {
  userId: PropTypes.string.isRequired,
};
